import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { listFarmerMonthlyReports } from '../lib/firestore';

function toStatusLabel(status) {
  if (status === 'pending') return '申請中';
  if (status === 'approved' || !status) return '承認済み';
  if (status === 'rejected') return '差し戻し';
  if (status === 'draft') return '下書き';
  return status;
}

export default function MeReportStatusPage() {
  const { userProfile } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const linkedFarmerId = userProfile?.linkedFarmerId || '';

  useEffect(() => {
    async function run() {
      if (!linkedFarmerId) {
        setReports([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const rows = await listFarmerMonthlyReports(linkedFarmerId, true);
        setReports(rows);
      } catch (err) {
        setError(`承認ステータスの取得に失敗しました。${err?.message ? ` ${err.message}` : ''}`);
      } finally {
        setLoading(false);
      }
    }
    run();
  }, [linkedFarmerId]);

  const statusSummary = useMemo(() => {
    return reports.reduce((acc, row) => {
      const key = row.approvalStatus || 'approved';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }, [reports]);

  return (
    <>
      <Helmet><title>Drs.Farm | 近況報告の承認ステータス</title></Helmet>
      <h1>近況報告の承認ステータス</h1>
      <section className="panel">
        <div className="row-actions">
          <Link className="btn btn-secondary" to="/me">農家さんメニューへ戻る</Link>
          <Link className="btn btn-primary" to={linkedFarmerId ? `/admin/reports?editor=farmer&mode=edit&farmerId=${encodeURIComponent(linkedFarmerId)}` : '/admin/reports?editor=farmer&mode=edit'}>
            近況報告の修正へ
          </Link>
        </div>
      </section>

      {!linkedFarmerId ? (
        <section className="panel">
          <p className="error-text">紐付け農家が未設定のため、承認ステータスを表示できません。</p>
        </section>
      ) : null}

      {error ? (
        <section className="panel">
          <p className="error-text">{error}</p>
        </section>
      ) : null}

      <section className="panel">
        <h2>集計</h2>
        {loading ? (
          <p>読み込み中...</p>
        ) : (
          <div className="tag-row">
            <span className="pill">申請中: {statusSummary.pending || 0}</span>
            <span className="pill">承認済み: {statusSummary.approved || 0}</span>
            <span className="pill">差し戻し: {statusSummary.rejected || 0}</span>
            <span className="pill">下書き: {statusSummary.draft || 0}</span>
          </div>
        )}
      </section>

      <section className="panel">
        <h2>一覧</h2>
        {loading ? (
          <p>読み込み中...</p>
        ) : reports.length === 0 ? (
          <p>近況報告はまだありません。</p>
        ) : (
          <ul className="directory-list">
            {reports.map((row) => (
              <li key={row.id} className="directory-row">
                <p><strong>{row.title || row.highlights || 'タイトル未設定'}</strong></p>
                <p className="directory-meta">日付: {row.publishedDate || row.month || '-'}</p>
                <p className="directory-meta">状態: {toStatusLabel(row.approvalStatus)}</p>
                <Link className="text-btn" to={`/admin/reports?editor=farmer&mode=edit&farmerId=${encodeURIComponent(linkedFarmerId)}&reportId=${encodeURIComponent(row.id)}`}>
                  この投稿を修正
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
